import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppButton, AppModal, Callout, FieldGroup, FormField, NativeSelect, Radio } from "../ui";
import GenerateAiQuestionsModal from "./GenerateAiQuestionsModal";
import { addMethodsFor, splitCategories, subtopicsOf } from "./quizBankRules";

/**
 * AddQuestionsDialog — "How do you want to add questions?"
 *
 * One question at a time, a spreadsheet, an AI draft or a passage: four ways
 * in, chosen in one place instead of four buttons a newcomer has to tell apart.
 * AI drafts into a subtopic, so choosing it asks for one here and then opens
 * the drafting dialog; the others go to their own guided pages.
 *
 *   base        "/quiz-bank" or the platform library's base
 *   categories  the flat category list (subtopics carry `parentCategory`)
 *   defaults    { category, subcategory } — the folder the bank has open
 *   onAiSaved   (message) => void, after AI questions were kept
 */
export default function AddQuestionsDialog({ show, onHide, base, canUseAi = false, categories = [], defaults = {}, onAiSaved }) {
  const navigate = useNavigate();
  const [method, setMethod] = useState("single");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [ai, setAi] = useState(null);

  useEffect(() => {
    if (!show) return;
    setMethod("single");
    setCategory(defaults.category || "");
    setSubcategory(defaults.subcategory || "");
  }, [show, defaults.category, defaults.subcategory]);

  const methods = addMethodsFor({ ai: canUseAi });
  const { top, subs } = splitCategories(categories);
  const subtopics = subtopicsOf(categories, category);
  const noSubtopics = method === "ai" && subs.length === 0;
  const aiReady = method !== "ai" || (category && subcategory);

  const go = () => {
    if (method === "ai") {
      const cat = top.find((c) => String(c._id) === String(category));
      const sub = subtopics.find((c) => String(c._id) === String(subcategory));
      if (!cat || !sub) return;
      onHide();
      setAi({ category: cat, subcategory: sub });
      return;
    }
    onHide();
    if (method === "upload") navigate(`${base}/bulk-upload`);
    else if (method === "passage") navigate(`${base}/passages/new`);
    else {
      const qs = new URLSearchParams();
      if (category) qs.set("category", category);
      if (category && subcategory) qs.set("subcategory", subcategory);
      const query = qs.toString();
      navigate(`${base}/add${query ? `?${query}` : ""}`);
    }
  };

  return (
    <>
      <AppModal
        show={show}
        onHide={onHide}
        title="Add questions"
        size="md"
        footer={
          <>
            <AppButton type="button" variant="outline" onClick={onHide}>Cancel</AppButton>
            <AppButton type="button" onClick={go} disabled={!aiReady || noSubtopics}>Continue</AppButton>
          </>
        }
      >
        <div className="tw-flex tw-flex-col tw-gap-4">
          <FieldGroup legend="How do you want to add them?">
            <div className="tw-flex tw-flex-col tw-gap-2">
              {methods.map((m) => (
                <Radio
                  key={m.key}
                  variant="card"
                  name="add-questions-method"
                  value={m.key}
                  checked={method === m.key}
                  onChange={() => setMethod(m.key)}
                  label={m.title}
                  description={m.text}
                />
              ))}
            </div>
          </FieldGroup>

          {method === "ai" && (noSubtopics ? (
            <Callout tone="info">
              AI drafts questions into a subtopic, and there are none yet. Open a category in the question bank and add a
              subtopic first.
            </Callout>
          ) : (
            <div className="tw-grid tw-gap-3 min-[560px]:tw-grid-cols-2">
              <FormField label="Category" required>
                <NativeSelect value={category} onChange={(e) => { setCategory(e.target.value); setSubcategory(""); }}>
                  <option value="">Choose a category</option>
                  {top.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </NativeSelect>
              </FormField>
              <FormField label="Subtopic" required>
                <NativeSelect value={subcategory} onChange={(e) => setSubcategory(e.target.value)} disabled={!subtopics.length}>
                  <option value="">{category && !subtopics.length ? "No subtopics in this category" : "Choose a subtopic"}</option>
                  {subtopics.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </NativeSelect>
              </FormField>
            </div>
          ))}
        </div>
      </AppModal>

      {ai && (
        <GenerateAiQuestionsModal
          category={ai.category}
          subcategory={ai.subcategory}
          onClose={() => setAi(null)}
          onSaved={(message) => { setAi(null); onAiSaved?.(message); }}
        />
      )}
    </>
  );
}
